import type { WaveDef } from '../schemas/defs.js';

/**
 * The authored 25-wave table (GDD §3).
 *
 * ── One table, not one per map ─────────────────────────────────────────────────────────
 * TRD-02 V-6 originally called for waves 1-25 per authored map — 75 wave definitions.
 * That was cut to a single shared table. Nothing in the GDD asks the three maps to differ
 * in composition; they differ in *layout*, which is the variable players actually engage
 * with. Three parallel tables would have tripled the balancing surface to re-express the
 * same difficulty curve three times.
 *
 * Daily runs do not use this at all — TRD-13 generates their waves from the seed. Both
 * producers emit the same `WaveDef` shape, so TRD-06 cannot tell them apart (R-02-09).
 *
 * Global HP scaling (`hp × (1 + 0.11 × (wave-1))`, TRD-06 R-06-10) is applied on top of
 * these counts at runtime, so `hpMultiplier` here is for per-group emphasis only.
 *
 * ⚠ PLACEHOLDER pacing. The archetype introduction order is deliberate; the numbers are not.
 */

function wave(w: number, groups: WaveDef['groups'], isBoss = false): WaveDef {
  return { wave: w, groups, isBoss };
}

export const WAVES: WaveDef[] = [
  // 1-4: runners only, then sprinters. Teach the loop before teaching a counter.
  wave(1, [{ enemy: 'runner', count: 6, spacing: 0.8, delay: 0, hpMultiplier: 1 }]),
  wave(2, [{ enemy: 'runner', count: 10, spacing: 0.7, delay: 0, hpMultiplier: 1 }]),
  wave(3, [
    { enemy: 'runner', count: 8, spacing: 0.7, delay: 0, hpMultiplier: 1 },
    { enemy: 'sprinter', count: 6, spacing: 0.35, delay: 4, hpMultiplier: 1 },
  ]),
  wave(4, [
    { enemy: 'runner', count: 10, spacing: 0.6, delay: 0, hpMultiplier: 1 },
    { enemy: 'brute', count: 2, spacing: 1.6, delay: 5, hpMultiplier: 1 },
  ]),

  // 5: Bulwark. Teaches armor pierce.
  wave(5, [{ enemy: 'boss-bulwark', count: 1, spacing: 1, delay: 1, hpMultiplier: 1 }], true),

  wave(6, [
    { enemy: 'runner', count: 12, spacing: 0.55, delay: 0, hpMultiplier: 1 },
    { enemy: 'brute', count: 3, spacing: 1.4, delay: 4, hpMultiplier: 1 },
  ]),
  wave(7, [
    { enemy: 'sprinter', count: 14, spacing: 0.3, delay: 0, hpMultiplier: 1 },
    { enemy: 'ward', count: 3, spacing: 1.5, delay: 6, hpMultiplier: 1 },
  ]),
  wave(8, [
    { enemy: 'runner', count: 12, spacing: 0.5, delay: 0, hpMultiplier: 1 },
    { enemy: 'splitter', count: 4, spacing: 1.2, delay: 5, hpMultiplier: 1 },
  ]),
  wave(9, [
    { enemy: 'brute', count: 5, spacing: 1.2, delay: 0, hpMultiplier: 1 },
    { enemy: 'ward', count: 4, spacing: 1.3, delay: 6, hpMultiplier: 1 },
  ]),

  // 10: Hive. Teaches AoE and slows against a burst of sprinters.
  wave(10, [{ enemy: 'boss-hive', count: 1, spacing: 1, delay: 1, hpMultiplier: 1 }], true),

  wave(11, [
    { enemy: 'mender', count: 3, spacing: 1.4, delay: 0, hpMultiplier: 1 },
    { enemy: 'runner', count: 16, spacing: 0.45, delay: 2, hpMultiplier: 1 },
  ]),
  wave(12, [
    { enemy: 'drifter', count: 6, spacing: 1.0, delay: 0, hpMultiplier: 1 },
    { enemy: 'sprinter', count: 16, spacing: 0.28, delay: 4, hpMultiplier: 1 },
  ]),
  wave(13, [
    { enemy: 'splitter', count: 6, spacing: 1.0, delay: 0, hpMultiplier: 1 },
    { enemy: 'brute', count: 4, spacing: 1.3, delay: 6, hpMultiplier: 1 },
  ]),
  wave(14, [
    { enemy: 'ward', count: 6, spacing: 1.1, delay: 0, hpMultiplier: 1 },
    { enemy: 'mender', count: 3, spacing: 1.5, delay: 5, hpMultiplier: 1 },
  ]),

  // 15: Warden-of-Rust. Teaches burst over sustained chip.
  wave(15, [{ enemy: 'boss-rust', count: 1, spacing: 1, delay: 1, hpMultiplier: 1 }], true),

  wave(16, [
    { enemy: 'runner', count: 20, spacing: 0.4, delay: 0, hpMultiplier: 1 },
    { enemy: 'drifter', count: 8, spacing: 0.9, delay: 5, hpMultiplier: 1 },
  ]),
  wave(17, [
    { enemy: 'brute', count: 7, spacing: 1.1, delay: 0, hpMultiplier: 1 },
    { enemy: 'splitter', count: 6, spacing: 1.0, delay: 6, hpMultiplier: 1 },
  ]),
  wave(18, [
    { enemy: 'sprinter', count: 24, spacing: 0.24, delay: 0, hpMultiplier: 1 },
    { enemy: 'ward', count: 6, spacing: 1.2, delay: 7, hpMultiplier: 1 },
  ]),
  wave(19, [
    { enemy: 'mender', count: 5, spacing: 1.2, delay: 0, hpMultiplier: 1 },
    { enemy: 'brute', count: 8, spacing: 1.0, delay: 3, hpMultiplier: 1 },
  ]),

  // 20: Chorus. A mender escorted by a crowd — teaches execution and target priority.
  wave(
    20,
    [
      { enemy: 'boss-chorus', count: 1, spacing: 1, delay: 1, hpMultiplier: 1 },
      { enemy: 'runner', count: 20, spacing: 0.5, delay: 3, hpMultiplier: 1 },
    ],
    true,
  ),

  wave(21, [
    { enemy: 'splitter', count: 10, spacing: 0.9, delay: 0, hpMultiplier: 1 },
    { enemy: 'drifter', count: 10, spacing: 0.8, delay: 5, hpMultiplier: 1 },
  ]),
  wave(22, [
    { enemy: 'brute', count: 10, spacing: 0.95, delay: 0, hpMultiplier: 1 },
    { enemy: 'ward', count: 8, spacing: 1.0, delay: 5, hpMultiplier: 1 },
  ]),
  wave(23, [
    { enemy: 'sprinter', count: 32, spacing: 0.2, delay: 0, hpMultiplier: 1 },
    { enemy: 'mender', count: 6, spacing: 1.1, delay: 6, hpMultiplier: 1 },
  ]),
  wave(24, [
    { enemy: 'brute', count: 12, spacing: 0.85, delay: 0, hpMultiplier: 1 },
    { enemy: 'splitter', count: 10, spacing: 0.85, delay: 4, hpMultiplier: 1 },
    { enemy: 'drifter', count: 10, spacing: 0.8, delay: 8, hpMultiplier: 1 },
  ]),

  // 25: Prism. Flying, stun-immune, enormous — a pure composition test.
  wave(
    25,
    [
      { enemy: 'boss-prism', count: 1, spacing: 1, delay: 2, hpMultiplier: 1 },
      { enemy: 'ward', count: 10, spacing: 0.9, delay: 6, hpMultiplier: 1 },
    ],
    true,
  ),
];
